import { CSSProperties } from 'react';
import { isEmbeddedInIframe } from 'utils/utils';
import { useVisibleViewport, VisibleViewport } from './useVisibleViewport';

export interface IframeDialogClamp {
	/** True when running inside the Punchup iframe (drives the iframe-only overlay class + body-scroll lock). */
	isInIframe: boolean;
	/**
	 * Inline style for the full-screen overlay: pins it to the parent's visible band. The `position`
	 * arg picks `absolute` (for overlays portalled to `document.body`) or `fixed` (for inline overlays).
	 * `undefined` when standalone or before the first `parent-scroll` (the CSS `position: fixed`
	 * fallback then applies).
	 */
	overlayStyle?: CSSProperties;
	/**
	 * Inline style for the dialog card: caps its height to the visible band. `max-height: 100%`
	 * resolves against the overlay's definite (border-box) height minus its padding, so the card
	 * can never exceed the fold and its own `overflow-y: auto` becomes the scroll container —
	 * keeping the action buttons reachable by dragging the card (not the backdrop, which dismisses).
	 * `undefined` in the same cases as `overlayStyle`.
	 */
	containerStyle?: CSSProperties;
	/**
	 * The raw parent-reported visible band. For consumers whose card is NOT a direct child of the overlay
	 * (e.g. a focus-trap wrapper sits between them), where a percentage `containerStyle` max-height can't
	 * resolve — build a px cap from `viewport.viewportHeight` instead. `undefined` in the same cases as
	 * `overlayStyle`.
	 */
	viewport?: VisibleViewport;
}

/**
 * Clamps a modal/dialog to the parent page's visible viewport when embedded in the Punchup iframe.
 *
 * The iframe is sized to its full document height (App.tsx posts `document.body.scrollHeight`), so
 * `position: fixed` anchors to the iframe document — not the visible fold — and CSS viewport units
 * (`vh`/`dvh`/`svh`) resolve against the whole document. A tall dialog therefore overflows the mobile
 * fold and its buttons become unreachable (TIC-636). The only reliable source of the visible band is
 * the `parent-scroll` protocol via {@link useVisibleViewport}; this hook turns that band into the two
 * inline styles a dialog needs (overlay position + card height cap).
 *
 * Consumers spread `overlayStyle` onto their overlay element and `containerStyle` onto their card, and
 * keep their own body-scroll lock / iframe class (gated on `isInIframe`). Standalone and desktop are
 * unaffected — both styles are `undefined`, leaving the component's CSS (real `vh`) in charge.
 *
 * @param enabled gate the underlying scroll listener so idle consumers (e.g. a closed dialog) don't
 *   subscribe to every parent scroll. Defaults to `true`.
 * @param position CSS positioning for the clamped overlay. Default `'absolute'` — correct for overlays
 *   PORTALLED to `document.body` (they anchor to the document origin). Pass `'fixed'` for overlays
 *   rendered INLINE (not portalled): `fixed` ignores positioned ancestors, so the clamp anchors to the
 *   iframe viewport wherever the overlay sits in the tree, whereas `absolute` would anchor to the nearest
 *   `position:relative`/`sticky` ancestor and mis-place the modal. Both resolve to the same visible band
 *   in the content-height iframe (which has no internal scroll and no `transform` ancestor).
 */
export const useIframeDialogClamp = (
	enabled = true,
	position: 'absolute' | 'fixed' = 'absolute'
): IframeDialogClamp => {
	const isInIframe = isEmbeddedInIframe();
	const viewport = useVisibleViewport(enabled);

	// `viewport` is non-null only inside the iframe once a parent-scroll has arrived — i.e. when the
	// parent has passed its visible-band coordinates. Until then (or standalone) we return no styles and
	// the component's CSS defaults hold. "Coordinates passed → clamp; not passed → defaults."
	if (!viewport) return { isInIframe };

	return {
		isInIframe,
		overlayStyle: {
			position,
			top: viewport.visibleTop,
			height: viewport.viewportHeight,
			bottom: 'auto',
		},
		containerStyle: { maxHeight: '100%' },
		viewport,
	};
};
